import { DateTime, DateTimeFormatOptions } from "luxon";

export const yearsAgo = (date?: DateTime, toDate?: DateTime): number => {
  if (date)
    return Math.floor(date.diff(toDate || DateTime.now(), "years").years * -1);
  return 0;
};

interface Created {
  created_at?: string;
}

export function mostRecentlyCreated<T extends Created>(
  array: T[] | undefined
): T | undefined {
  return array
    ? array.reduce((acc: T | undefined, e: T) => {
        if (
          !acc ||
          DateTime.fromISO(e.created_at || "") >
            DateTime.fromISO(acc.created_at || "")
        ) {
          return e;
        }
        return acc;
      }, undefined)
    : undefined;
}

export const getSlideRetentionDate = (
  date: DateTime,
  today: DateTime = DateTime.now()
) => {
  if (date > today) {
    const daysDifference = Math.floor(date.diff(today, "days").days);
    return daysDifference >= 1
      ? `Available for ${daysDifference} day${daysDifference === 1 ? "" : "s"}`
      : "Available for today";
  } else {
    return `Archived ${date.toLocaleString(DateTime.DATE_SHORT)}`;
  }
};

// For use with Luxon
// Matches the format accepted by datetime-local input
// https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/datetime-local#max
// Described by MDN as YYYY-MM-DDThh:mm
export const HTML_DATETIME_LOCAL_INPUT_FORMAT = "yyyy-MM-dd'T'HH:mm";

export const getDateTimefromTimestamp = (
  date: string,
  zone?: string | null
) => {
  return DateTime.fromISO(date, {
    zone: zone || DateTime.local().zone,
  });
};

export const getLocalizedStringWithZone = (
  date: DateTime,
  format: DateTimeFormatOptions,
  zone?: string | null
) => {
  return `${date.toLocaleString(format)} ${getZoneString(zone)}`;
};

// Formats a timestamp for a datetime input, if no zone is given, formats it to the local time.
export const formatTimestampForDateTime = (
  date: string,
  zone?: string | null
) => {
  return getDateTimefromTimestamp(date, zone).toFormat(
    HTML_DATETIME_LOCAL_INPUT_FORMAT
  );
};

// A simple helper function to normalize zone strings for display around the websites
export const getZoneString = (zone?: string | null): string => {
  return zone || DateTime.local().zoneName;
};

// Formats a date string into an API ready ISO timestamp
// toJSDate() is used due to the differing ways the DateTime and JS Date objects format their ISO timestamps.
//   JS Date objects format correctly for the API as opposed to DateTime.
// TODO: Explore if there is a means to use only DateTime objects here in order to create an API ready timestamp.
export const prepareDatetimeStringForAPI = (
  date: string,
  zone?: string | null
) => {
  return DateTime.fromFormat(date, HTML_DATETIME_LOCAL_INPUT_FORMAT, {
    zone: zone || DateTime.local().zone,
  }).toISO();
};

// timestamp/dateTimeToLocaleDateTimeString()
// Formats a timestamp string or a DateTime object into a string with the format "7/5/1989, 11:01:42 AM CDT"
export const timestampToLocaleDateTimeString = (
  timestamp: string,
  zone?: string | null
) => {
  return dateTimeToLocaleDateTimeString(
    getDateTimefromTimestamp(timestamp, zone)
  );
};

export const dateTimeToLocaleDateTimeString = (dateTime: DateTime) => {
  return `${dateTime.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)} ${
    dateTime.offsetNameShort
  }`;
};

// timestamp/dateTimeToLocaleDateString()
// Formats a timestamp string or a DateTime object into a string with the format "7/5/1989"
export const timestampToLocaleDateString = (
  timestamp: string,
  zone?: string | null
) => {
  return dateTimeToLocaleDateString(getDateTimefromTimestamp(timestamp, zone));
};

export const dateTimeToLocaleDateString = (dateTime: DateTime) => {
  return dateTime.toLocaleString(DateTime.DATE_SHORT);
};
export const getLocalizedTime = (isoTime: string, timezone: string) =>
  DateTime.fromISO(isoTime, { zone: timezone })
    .setZone("local")
    .toFormat("yyyy-MM-dd'T'HH:mm");
export const prepReceivedDateMin = (collectedDate: string) =>
  DateTime.fromISO(collectedDate).plus({ minute: 1 }).toISODate();

export const diffStringTemplate = (
  unitOfTime: number,
  type: "Day" | "Hour" | "Minute"
) => `${unitOfTime} ${type}${unitOfTime === 1 ? "" : "s"} Ago`;

export type DateDiff = {
  timeStr: string;
  hours: number;
};

export const getDateDiff = (
  startDate: DateTime,
  endDate?: DateTime,
  useMinutes?: boolean
): DateDiff | null => {
  endDate = endDate ?? DateTime.now();
  // Seconds is included to make sure minutes is 'floored'. Each subsequent unit of time floors the previous.
  const diff = endDate.diff(startDate, ["days", "hours", "minutes", "seconds"]);
  const totalHoursDiff = (diff.days ?? 0) * 24 + (diff.hours ?? 0);

  if (!diff) return null;

  if (diff.days > 0)
    return {
      timeStr: diffStringTemplate(diff.days, "Day"),
      hours: totalHoursDiff,
    };
  if (diff.hours > 0)
    return {
      timeStr: diffStringTemplate(diff.hours, "Hour"),
      hours: totalHoursDiff,
    };
  if (diff.minutes > 0 && useMinutes)
    return { timeStr: diffStringTemplate(diff.minutes, "Minute"), hours: 0 };

  return null;
};
